import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { mdiCalculator, mdiCheckCircle, mdiAlertCircle } from '@mdi/js';
import Icon from '@mdi/react';
import { IWscVmProps, WaterwaySizingCalculatorVM } from './WaterwaySizingCalculatorVM';
import { WaterWaySizingCalculatorDetails } from './Details';
import { Accordion } from 'cd-eft/components/common/Accordion';
import { VegetalCoverComponent } from './VegetalCoversComp/VegetalCovers';
import { ChannelFlowCalculator } from './ChannelFlowForm';
import { SoilStrengthCalculator } from './SoilStrengthForm/SoilStrengthForm';
import { PrintComponent } from 'cd-eft/components/common/PrintPDF';
import { TablePrintComponent } from './PrintTemplate';
import { Modal, ModalHeader, ModalBody, ModalFooter, Container, Row } from 'reactstrap';
import { LoadingSpinner } from 'cd-eft/components/common/LoadingSpinner/LoadingSpinner';
import './WaterwaySizingCalculator.scss';
import stores from 'cd-eft/models/Stores';
const { waterwayDesignStore } = stores.AppStore;

interface IResultsColumnProps {
  vm: WaterwaySizingCalculatorVM;
}
interface IResultItemProps {
  title: string;
  value?: string;
  condition?: boolean;
}
interface IResultSetProps {
  header: string;
  items: IResultItemProps[];
  containerClass: string;
}
const ResultItem = (props: IResultItemProps) => {
  const conditional = props.condition === undefined ? true : props.condition;
  return (
    <div className="result-item">
      <div className='result-title nobr'>
        <strong>{props.title}</strong>
      </div>
      {conditional ? <div className="result-value">{props.value}</div> : null}
    </div>
  )
}
const ResultSet = (props: IResultSetProps) => {
  return (
    <div className={props.containerClass}>
      <h3 className="wsc-section-header">{props.header}</h3>
      <div className="result-summary-columns">
        {props.items.map((item, index) => (
          <ResultItem key={`result-item-${index}`} title={item.title} value={item.value} condition={item.condition} />
        )
        )}
      </div>
    </div>
  )
}
const ResultsColumn = (props: IResultsColumnProps) => {
  const vm = props.vm;
  const isError = waterwayDesignStore.WSCstatus === 'ERROR_STATUS';
  const errorCodes = waterwayDesignStore.WSCstatusErrorCode?.split('\n').filter(code => code.trim().length > 0) || [];
  const formattedErrorCode = errorCodes.length > 0 ? errorCodes.join(', ') : waterwayDesignStore.WSCstatusErrorCode;

  return (
    <>
      <div className="pt-4 pb-1">
        <h3 className={`mb-3 ${isError ? 'error-h3' : 'success-h3'}`}>
          Calculation Results: <span className="nobr">{isError ? 'Failed' : 'Successful'}<Icon path={isError ? mdiAlertCircle : mdiCheckCircle} size={1}></Icon></span>
        </h3>
        {(waterwayDesignStore.openedFromWarningIcon && waterwayDesignStore.WSCstatusErrorCode || waterwayDesignStore.WSCstatusErrorMessage) && (
          <div className="error-info mt-2">
            <p><strong>Error Code: {formattedErrorCode}</strong></p>
            <p><strong>
              {waterwayDesignStore.WSCstatus === 'WARNING_STATUS' ? 'Warning Message: ' : 'Error Message: '}
              {waterwayDesignStore.WSCstatusErrorMessage}
            </strong></p>
          </div>
        )}
        {!(waterwayDesignStore.openedFromWarningIcon && waterwayDesignStore.hasChannelDimensionsError) && (
            <p>No calculation errors. Design channel is {vm.calculationResult?.dataVertex?.simWidth} ft wide x {vm.calculationResult?.dataVertex?.simDepth} ft deep.</p>
        )}      
      </div>
      <div className="usa-summary-box sub-container">

        <div className="sub-container-content usa-summary-box__body">
          <div className="result-subcontainer-section">
            <ResultSet
              header='Stability Flow Conditions'
              containerClass='stability-flow-conditions'
              items={[{
                title: "Manning's n:",
                value: vm.calculationResult?.stabNString
              },
              {
                title: "Allowable Stress:",
                value: vm.calculationResult?.soilParameters?.allowableStress?.toString()
              }, {
                title: "Soil Grain Roughness:",
                value: vm.calculationResult?.soilParameters?.soilGrainRoughness?.toString()
              }, {
                title: 'Avg. Velocity (ft/sec):',
                value: vm.calculationResult?.stabVString
              }, {
                title: 'Hydraulic Radius (ft):',
                value: vm.calculationResult?.stabHydRadiusString
              }, {
                title: 'X-sect. Area (sq.ft)',
                value: vm.calculationResult?.stabXSectAreaString
              }, {
                title: 'Flow Width (ft):',
                value: vm.calculationResult?.stabFlowWidthString
              },
              {
                title: 'Flow Width w/Fb (ft):',
                value: vm.calculationResult?.stabFlowWidthWFBString
              },
              {
                title: 'Bed Width (ft):',
                value: vm.calculationResult?.stabBedWidthString,
                condition: vm.calculationResult?.channelParams?.channelShapeStr === 'TRAPEZOIDAL'
              },
              {
                title: 'Flow Depth (ft):',
                value: vm.calculationResult?.stabFlowDepthString
              },
              {
                title: 'P-Channel Coefficient:',
                value: vm.calculationResult?.stabBedWidthString,
                condition: vm.calculationResult?.channelParams?.channelShapeStr === 'PARABOLIC'
              }
              ]}
            />
            {/* <hr></hr> */}
            <ResultSet
              header='Capacity Flow Conditions'
              containerClass='capacity-flow-conditions'
              items={[{
                title: "Manning's n:",
                value: vm.calculationResult?.capNString
              }, {
                title: 'Avg. Velocity (ft/sec):',
                value: vm.calculationResult?.capVString
              }, {
                title: 'Hydraulic Radius (ft):',
                value: vm.calculationResult?.capHydRadiusString
              }, {
                title: 'X-sect. Area (sq.ft)',
                value: vm.calculationResult?.capXSectAreaString
              }, {
                title: 'Flow Width (ft):',
                value: vm.calculationResult?.capFlowWidthString
              },
              {
                title: 'Flow Width w/Fb (ft):',
                value: vm.calculationResult?.capFlowWidthWFBString
              },
              {
                title: 'Bed Width (ft):',
                value: vm.calculationResult?.capBedWidthString,
                condition: vm.calculationResult?.channelParams?.channelShapeStr === 'TRAPEZOIDAL'
              },
              {
                title: 'Flow Depth (ft):',
                value: vm.calculationResult?.capFlowDepthString
              },
              {
                title: 'P-Channel Coefficient:',
                value: vm.calculationResult?.capBedWidthString,
                condition: vm.calculationResult?.channelParams?.channelShapeStr === 'PARABOLIC'
              }
              ]}
            />
            {/* <hr></hr> */}
            <ResultSet
              header='Parabolic Offset Depth'
              containerClass='parabolic-offset-depth'
              items={[{
                title: "Parabolic offset 1 (%):",
                value: `${vm.calculationResult?.channelParams?.offset1! * 100}`
              }, {
                title: 'Parabolic offset 2 (%):',
                value: `${vm.calculationResult?.channelParams?.offset2! * 100}`
              }, {
                title: 'Parabolic offset 3 (%):',
                value: `${vm.calculationResult?.channelParams?.offset3! * 100}`
              }
              ]}
            />
          </div>
        </div>
      </div>
    </>
  )
}
const ReachNameCard = observer( () => {
  return (
    <div className="reach-name-card">
      <h3 className="wsc-section-header">Station: {waterwayDesignStore.WSCreachNames}</h3>
    </div>
  );
});

const WaterwaySizingCalculatorComp = (props: IWscVmProps) => {

  const vm = useRef(new WaterwaySizingCalculatorVM(props)).current;


  useEffect(() => {
    if (vm && vm.soilStrengthVM && vm.vegetalCoverVM) {
      vm.initializeParameters();
    }
  }, []);

  useEffect(() => {
    if(vm.mode === 'adminGlobalPreferences' && Object.keys(props.prefJson).length>0){
      vm.prefId = props.prefId
      vm.perfJson = JSON.parse(props.prefJson.preferenceValueJson);
      vm.getReqValuesForGlobalPreferences();
    }
  }, [props.prefJson, props.prefId]);

  useEffect(() => {
    waterwayDesignStore.wscComponentRef = vm;
    
    return () => {
      waterwayDesignStore.wscComponentRef = null;
    };
  }, [vm]);

  return (
    <div className="waterway-comp-container px-3">

      {vm.mode === 'design' ? <>
          {vm.waterWayWizardStatus === 2 ? (
            <div className="main-accordions pt-3">
              <ReachNameCard />
              <Accordion
                className={`p-0 m-0 accordion-wrapper ${waterwayDesignStore.WSCstatus === 'ERROR_STATUS' ? 'red-accordion' : 'green-accordion'}`}
                headers={['Summary Results']}
                aria-label={'Summary Results'}
                iconPathQ={false}
              >
                  <ResultsColumn vm={vm} />
              </Accordion>
            </div>
          ) : null}
      </> :
        vm.mode === 'standalone'?
        <h1 className="m-0 pt-3" aria-label="Waterway Sizing Calculator">
          Waterway Sizing Calculator
        </h1> : null}

        {vm.onClickInformation ? (
          <Modal
            isOpen={vm.onClickInformation}
            toggle={vm.onClickInformationToggle}
            centered={true}
            className="eft-action-modal"
            aria-label={vm.headerTitle}
          >
            <ModalHeader toggle={vm.onClickInformationToggle} className='header-info-detail' >
              <div className="display-flex flex-align-center">
                <strong >{`Information details on ${vm.headerTitle}`}</strong>
              </div>
              <a className="href-info-detail" href="/analysis/waterway-sizing-calculator/details" target="_blank">
                Open Information in a New Tab
              </a>
            </ModalHeader>
            <ModalBody>
              <Container>
                <WaterWaySizingCalculatorDetails title={vm.headerTitle} />
              </Container>
            </ModalBody>
            <ModalFooter>
              <button className="usa-button usa-button--outline" onClick={vm.onClickInformationToggle}>
                Close
              </button>
            </ModalFooter>
          </Modal>
        ) : null}
        
      <Row className='p-2'>
        <div className={`p-2 ${vm.mode === 'standalone' || vm.mode ==='adminGlobalPreferences'? "left-cards" : 'main-accordions'}`}>
        {vm.mode === 'design' && vm.waterWayWizardStatus !== 2 && <ReachNameCard />}
        <Accordion
            className={vm.waterWayWizardStatus == 2 ? `p-0 accordion-wrapper green-accordion` : `p-0 accordion-wrapper`}
            headers={['Define Soil Strength']}
            waterwayWizardStatus={vm.waterWayWizardStatus}
            onClickInformationToggle={vm.onClickInformationToggle}
            aria-label={'Define Soil Strength'}
            iconPathQ={true}
          >
            <SoilStrengthCalculator vm={vm} />
          </Accordion>

          <Accordion
            className={vm.waterWayWizardStatus == 2 ? `p-0 accordion-wrapper green-accordion` : `p-0 accordion-wrapper`}
            headers={['Define Vegetal Cover']}
            waterwayWizardStatus={vm.waterWayWizardStatus}
            onClickInformationToggle={vm.onClickInformationToggle}
            iconPathQ={true}
          >
            <VegetalCoverComponent vm={vm} />
          </Accordion>

          <Accordion
            className={`p-0 m-0 accordion-wrapper ${vm.waterWayWizardStatus == 2 ? `green-accordion` : ``}`}
            headers={['Define Channel & Flow']}
            waterwayWizardStatus={vm.waterWayWizardStatus}
            onClickInformationToggle={vm.onClickInformationToggle}
            iconPathQ={true}
          >
            {/* //add Define Channel and flow component here */}
            <ChannelFlowCalculator vm={vm} />
          </Accordion>
        </div>

      {vm.mode === 'standalone' ?
        <div className="right-content p-2">
          <div className='sticky-content'>
            <div className="usa-card__container result-card">
              <div className="text-right nobr">
                <button className="usa-report-button usa-button usa-button--outline"
                  onClick={vm.onClickReportToggle}
                  disabled={vm.waterWayWizardStatus !== 2}
                >
                  Report
                </button>
                <button className="usa-button usa-button--outline m-0" onClick={vm.handleReset}>
                  Reset
                </button>
              </div>
              {vm.waterWayWizardStatus === 2 && vm.mode === 'standalone' ? (
                <ResultsColumn vm={vm} />
              ) : null}
            </div>
            <div className="mx-3">
              <button className={`usa-button w-100 mt-3 py-2 ${vm.waterWayWizardStatus === 2 ? 'result-card-button ' : ''}`} onClick={vm.finalCalculation}
                disabled={vm.calculateLock}>
                <Icon path={mdiCalculator} size={1} /> Calculate
              </button>
            </div>
          </div>
        </div> :
        vm.mode === 'design'?
        <div className={'wsc-fixed-control-buttons'}>
          <button className='usa-button usa-button--outline' onClick={vm.handleCloseDesignCaclulator}>Close</button>
          <button className='usa-button usa-button--outline calculation-button nobr' onClick={vm.finalCalculation}
            disabled={vm.calculateLock}><Icon path={mdiCalculator} size={1}></Icon>Calculate</button>
          <button
            className='usa-button px-2 m-0'
            onClick={vm.handleApplyPropertiesToSelectedReaches}
            disabled={vm.waterWayWizardStatus !== 2}
          >
            Apply Properties to Selected Reaches
          </button>
        </div> : null}
      </Row>
      {vm.mode === 'adminGlobalPreferences'?
        <span className='wsc-global-pref-buttons'>
          <button className="usa-button usa-button usa-button--outline" data-testid='global-pref-cancel-button' onClick={vm.cancelGlobalPref}>Cancel</button>
          <button className="usa-button" data-testid='global-pref-save-button' onClick={vm.saveGlobalPref} >Save</button>
        </span> :
      null}

      {vm.isLoading && <LoadingSpinner message="Save in progress" loading={vm.isLoading} className="download-project-loader" />}

      {/* //open the Popup window when user clicks report button */}
      <Modal className="project-owner-modal" isOpen={vm.saveButtonPopup}>
        <ModalBody>
          <PrintComponent
            componentToPrint={TablePrintComponent}
            printButtonText={'Report'}
            functionProp={vm}
            printButtonClasses={"usa-button usa-button--outline"}
            //componentProps is used to pass the calculation results to the pdf
            componentProps={{ inputs: vm.lastCalcPayload, outputs: vm.calculationResult, others: vm.lastOthersObj }}
          />
        </ModalBody>
      </Modal>
    </div>
  );
};

export const WaterwaySizingComp = observer(WaterwaySizingCalculatorComp);
